import { useState, useRef, ReactNode } from 'react';

export type AccordionItem = {
    title: string;
    content: ReactNode;
};

type AccordionProps = {
    items: AccordionItem[];
    className?: string;
    titleClassName?: string;
    contentClassName?: string;
};

/**
 * アコーディオン
 * @param items アコーディオンの項目
 * @param className アコーディオンのクラス名
 * @param titleClassName タイトルのクラス名
 * @param contentClassName コンテンツのクラス名
 */
export function Accordion({
    items,
    className = '',
    titleClassName = '',
    contentClassName = '',
}: AccordionProps) {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    const toggleAccordion = (index: number) => {
        const newOpenIndex = openIndex === index ? null : index;
        setOpenIndex(newOpenIndex);

        if (newOpenIndex !== null) {
            // アコーディオンが開くアニメーションが終わってからスクロールする
            setTimeout(() => {
                const element = itemRefs.current[index];
                if (element) {
                    const headerHeight = 300;
                    const elementPosition =
                        element.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - headerHeight;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                    });
                }
            }, 300); // transition-all duration-300
        }
    };

    return (
        <div className={`w-full ${className}`}>
            {items.map((item, index) => (
                <div
                    key={index}
                    ref={(el) => {
                        itemRefs.current[index] = el;
                    }}
                >
                    {/* タイトル */}
                    <button
                        className={`w-full flex justify-between items-centertransition-colors ${titleClassName}`}
                        onClick={() => toggleAccordion(index)}
                    >
                        <span>{item.title}</span>
                        {/* アイコン */}
                        <svg
                            className={`w-6 h-6 transform transition-transform duration-300 ${
                                openIndex === index ? 'rotate-180' : ''
                            }`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="gray"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    </button>
                    {/* コンテンツ */}
                    <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            openIndex === index ? '' : 'max-h-0'
                        }`}
                    >
                        {typeof item.content === 'string' ? (
                            <div
                                className={`${contentClassName}`}
                                dangerouslySetInnerHTML={{ __html: item.content }}
                            />
                        ) : (
                            <div className={`${contentClassName}`}>{item.content}</div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
